import { useEffect, useRef, useState, type ReactNode } from 'react'
import styles from './Reveal.module.css'

interface RevealProps {
  children: ReactNode
  className?: string
  /** Stagger delay in milliseconds. */
  delayMs?: number
}

/**
 * Scroll-in reveal. Progressive enhancement only:
 *
 * - Base CSS keeps content fully visible. If JS never runs, or the user
 *   prefers reduced motion, everything shows immediately.
 * - When motion is allowed, content starts offset + faded and transitions in
 *   once it scrolls into view (IntersectionObserver).
 * - A timeout backstop guarantees content is never trapped hidden.
 */
export function Reveal({ children, className, delayMs = 0 }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  // Start visible when we can't observe scrolling at all.
  const [shown, setShown] = useState(() => typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true)
          observer.disconnect()
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    )
    observer.observe(node)

    const backstop = window.setTimeout(() => setShown(true), 1500)

    return () => {
      observer.disconnect()
      window.clearTimeout(backstop)
    }
  }, [])

  const className_ = [styles.reveal, shown ? styles.shown : '', className]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={ref}
      className={className_}
      style={delayMs ? { transitionDelay: `${delayMs}ms` } : undefined}
    >
      {children}
    </div>
  )
}
